import { addQueryParam } from "@/lib/utils";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";
import { type AxiosResponse } from "axios";
import {
  apiClient,
  type ApiPaginatedResponse,
  type ApiResponse,
} from "./_client";
import { GlobalSearchResultItem } from "./organizations";

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  context_items: GlobalSearchResultItem[];
  created_by?: string;
  users: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    invitation_email: string | null;
  }[];
  organization: string;
}

export interface ObjectContext {
  object_type: string; // e.g., "widget", "dashboard", "project"
  object_id: string;
  object_name: string;
  action?: string; // e.g., "Updating", "Configuring"
}

export interface ChatMessageType {
  id?: string;
  sender?: "human" | "ai" | "event";
  content: string;
  timestamp?: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  sources: {
    id: string;
    filename: string;
  }[];
  attachments?: {
    id?: string;
    filename: string;
    mime_type: string;
    data: string;
    size?: number;
  }[];
  tool_results?: (
    | string
    | { output?: string; error?: string }
    | ToolResultCard
  )[];
  cache_invalidations?: string[][];
  object_context?: ObjectContext;
  escalation?: {
    original_prompt: string;
    dashboard_id: string;
    dashboard_name?: string;
    widget_id?: string;
    widget_name?: string;
  };
  scope_dashboard_id?: string;
  suggested_replies?: string[];
  events?: {
    context_added?: {
      type: string;
      item: {
        id: string;
        name: string;
      };
    }[];
    context_removed?: {
      type: string;
      item: {
        id: string;
        name: string;
      };
    }[];
    user_added?: {
      type: string;
      item: {
        id: string;
        name: string;
      };
    }[];
    user_removed?: {
      type: string;
      item: {
        id: string;
        name: string;
      };
    }[];
    is_typing?: boolean;
  };
}

export interface ToolResultCard {
  card_type: string;
  data: Record<string, unknown>;
  label?: string;
}

export const sendChatMessage = async (sessionId: string, message: string) => {
  const response = await apiClient.post<never, ApiResponse<ChatMessageType>>(
    `api/documents/chat-sessions/${sessionId}/messages/`,
    {
      message,
    },
  );
  return response;
};

export const newChatSessionFromDocument = async (documentId: string) => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/documents/chat-sessions/`,
    {
      documents: [documentId],
    },
  );
  return response;
};

export const newEmptyChatSession = async (title: string, projectId?: string) => {
  const response = await apiClient.post<never, ApiResponse<ChatSession>>(
    `api/documents/chat-sessions/`,
    {
      title,
      ...(projectId ? { projects: [projectId] } : {}),
    },
  );
  return response;
};

export const getOneChatSession = async (id: string) => {
  const response = await apiClient.get<never, AxiosResponse<ChatSession>>(
    `api/documents/chat-sessions/${id}/`,
  );
  return response.data;
};

export const getChatHistory = async (
  id: string,
  sinceTimeStamp: string | null = null,
): Promise<ChatMessageType[]> => {
  let url = `api/documents/chat-sessions/${id}/messages/`;
  if (sinceTimeStamp) {
    url = addQueryParam(url, "since_timestamp", sinceTimeStamp);
  }
  const response = await apiClient.get<never, AxiosResponse<ChatMessageType[]>>(
    url,
  );
  return response.data;
};

export const useChatSessions = ({
  pageSize = 10,
  pageIndex = 1,
  filter,
  ordering,
  projectId,
  enabled = true,
}: {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  ordering?: ColumnSort;
  projectId?: string;
  enabled?: boolean;
}): UseQueryResult<ApiPaginatedResponse<ChatSession>> => {
  let url = `api/documents/chat-sessions/`;
  if (filter) {
    url = addQueryParam(url, "search", filter);
  }
  if (ordering) {
    url = addQueryParam(
      url,
      "ordering",
      `${ordering.desc ? "-" : ""}${ordering.id}`,
    );
  }
  if (projectId) {
    url = addQueryParam(url, "project", projectId);
  }
  const q = useQuery<ApiPaginatedResponse<ChatSession>>({
    queryKey: [
      "chat-sessions",
      { pageSize, pageIndex, filter, ordering, projectId },
    ],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<ApiPaginatedResponse<ChatSession>>
      >(url);
      return response.data;
    },
    enabled,
  });
  return q;
};

export const deleteChatSession = async (id: string) => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/chat-sessions/${id}/`,
  );
  return response;
};

export const addMemberToChatSession = async ({
  sessionId,
  userId,
}: {
  sessionId: string;
  userId: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/documents/chat-sessions/${sessionId}/members/`,
    {
      user_id: userId,
    },
  );
  return response;
};

export const removeMemberFromChatSession = async ({
  sessionId,
  userId,
}: {
  sessionId: string;
  userId: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/chat-sessions/${sessionId}/members/`,
    { data: { user_id: userId } },
  );
  return response;
};

export const addContextItemToChatSession = async ({
  sessionId,
  itemType,
  itemId,
}: {
  sessionId: string;
  itemType: string;
  itemId: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/documents/chat-sessions/${sessionId}/context-items/`,
    {
      item_type: itemType,
      item_id: itemId,
    },
  );
  return response;
};

export const removeContextItemFromChatSession = async ({
  sessionId,
  itemType,
  itemId,
}: {
  sessionId: string;
  itemType: string;
  itemId: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/chat-sessions/${sessionId}/context-items/`,
    { data: { item_type: itemType, item_id: itemId } },
  );
  return response;
};
